import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  CalendarDays,
  Tag, 
  Users, 
  Star, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Image,
  Images,
  Map,
  Truck,
  Heart,
  Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminEmail, setAdminEmail] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminEmail(response.data.email);
      } catch (err) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    };

    verifyToken();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/calendar', icon: CalendarDays, label: 'Calendario' },
    { path: '/admin/bookings', icon: Calendar, label: 'Reservas' },
    { path: '/admin/tours', icon: Map, label: 'Tours' },
    { path: '/admin/tuktuks', icon: Truck, label: 'Tuk-Tuks' },
    { path: '/admin/gallery', icon: Images, label: 'Galeria' },
    { path: '/admin/media', icon: Image, label: 'Media' },
    { path: '/admin/discounts', icon: Tag, label: 'Descuentos' },
    { path: '/admin/referrals', icon: Users, label: 'Referidos' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/tips', icon: Heart, label: 'Propinas' },
    { path: '/admin/emails', icon: Mail, label: 'Emails' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          className="p-2 hover:bg-[#FDFBF7] rounded-lg"
        >
          {isMobileSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <span className="font-display font-bold text-[#C2410C]">Admin Panel</span>
        <Button variant="ghost" size="icon" onClick={handleLogout}>
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full bg-[#1C1917] text-white transition-all duration-300 flex flex-col ${
          isSidebarOpen ? 'w-64' : 'w-20'
        } ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo - fixed top */}
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <h1 className={`font-display font-bold text-xl ${!isSidebarOpen && 'hidden'}`}>
            Madrid Tuk Tuk
          </h1>
          <p className={`text-white/60 text-sm mt-1 ${!isSidebarOpen && 'hidden'}`}>
            Admin Panel
          </p>
        </div>

        {/* Navigation - scrollable */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-[#C2410C] text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isSidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User Info - fixed bottom */}
        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <div className={`flex items-center gap-3 ${!isSidebarOpen && 'justify-center'}`}>
            <div className="w-10 h-10 rounded-full bg-[#C2410C] flex items-center justify-center text-white font-bold flex-shrink-0">
              {adminEmail.charAt(0).toUpperCase()}
            </div>
            {isSidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{adminEmail}</p>
                <button
                  onClick={handleLogout}
                  className="text-xs text-white/60 hover:text-white flex items-center gap-1"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="h-3 w-3" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Toggle Sidebar (Desktop) */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-[#C2410C] rounded-full items-center justify-center text-white hover:bg-[#9A3412] transition-colors"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${isSidebarOpen && 'rotate-180'}`} />
        </button>
      </aside>

      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main
        className={`transition-all duration-300 ${
          isSidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        } pt-16 lg:pt-0`}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
